import { useEffect, useState } from "react";
// 배럴(@/api) 대신 모듈을 직접 가져온다. 배럴 importer 를 늘리지 않는다(scripts/check-boundaries).
import { listTrainingGroups, type TrainingGroup } from "@/api/trainingGroup";
import {
  addSharedWorkspace,
  createTrainingWorkspace,
  getTrainingWorkspaceSyncStatus,
  isWorkspaceAlreadyClaimedError,
  isWorkspaceNotInScopeError,
  listSharedWorkspaces,
  removeSharedWorkspace,
  type SharedWorkspace,
  type TrainingWorkspaceCandidate,
} from "@/api/trainingWorkspace";
import { resolveLoadErrorMessage } from "@/components/dataset/loadState";

/**
 * 그룹 공용 Workspace 의 서버 상태와 그 위의 쓰기.
 *
 * Owns:
 * - 현황·후보·Team 세 목록 로드(병렬 1회)와 축별 실패 분리.
 * - 등록·해제·생성+등록 요청과, 그 결과로 세 목록을 갱신하는 규칙.
 * - 거절 코드(미매핑·선점)를 사유 문구로 바꾸는 매핑.
 *
 * Does not own:
 * - 고른 후보·열린 모달·입력값 같은 화면 상태. 이 상태는 모달이 가진다.
 * - toast 표시. 쓰기는 결과만 반환하고, 어디에 무엇을 표시할지는 호출부가 정한다. 같은 실패라도
 *   [등록] 버튼을 가리키는 문장은 그 버튼을 아는 쪽이 쓴다.
 */

export type GroupOption = { value: string; label: string };

// 쓰기 결과. 실패하면 사유 문구를 함께 반환한다(호출부가 표시 위치를 정한다).
export type SharedWorkspaceWrite = { ok: true } | { ok: false; message: string };

/**
 * 생성+등록 결과. `orphan` 이 있으면 **생성은 성공하고 등록만 실패한 것**이다. 같은 이름으로 다시
 * 만들면 중복(409)이 된다. 그래서 호출부는 그 workspace 를 선택한 상태로 [등록]을 이어서 해야 한다.
 */
export type SharedWorkspaceCreate =
  | { ok: true }
  | { ok: false; message: string; orphan: TrainingWorkspaceCandidate | null };

// 공용 workspace 를 여는 Team 의 이름. BE 의 RESEARCH_GROUP_NAME 과 같다. 새 Workspace 를 만들 때
// Team 을 미리 고르는 힌트일 뿐이고, 자격 판정은 BE 가 한다(없으면 사용자가 고른다).
const RESEARCH_TEAM_NAME = "research";

export function useSharedWorkspaces() {
  const [shared, setShared] = useState<SharedWorkspace[]>([]);
  const [candidates, setCandidates] = useState<TrainingWorkspaceCandidate[]>([]);
  const [groups, setGroups] = useState<TrainingGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // 후보·Team 조회 실패가 현황 표시를 막지 않도록 따로 안내한다(빈 목록과 실패를 구분한다).
  const [candidateError, setCandidateError] = useState<string | null>(null);
  const [groupError, setGroupError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    void Promise.allSettled([
      listSharedWorkspaces(),
      getTrainingWorkspaceSyncStatus(),
      listTrainingGroups(),
    ]).then(([sharedResult, syncResult, groupsResult]) => {
      if (!alive) return;
      if (sharedResult.status === "fulfilled") {
        setShared(sharedResult.value);
      } else {
        setLoadError(resolveLoadErrorMessage(sharedResult.reason));
      }
      if (syncResult.status === "fulfilled") {
        setCandidates(syncResult.value.unclaimed);
      } else {
        setCandidateError(resolveLoadErrorMessage(syncResult.reason));
      }
      if (groupsResult.status === "fulfilled") {
        setGroups(groupsResult.value);
      } else {
        setGroupError(resolveLoadErrorMessage(groupsResult.reason));
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const sharedIds = new Set(shared.map((row) => row.training_workspace_id));
  const candidateOptions = candidates
    .filter((candidate) => !sharedIds.has(candidate.id))
    .map((candidate) => ({ value: candidate.id, label: candidate.name }));

  // 생성 대상 Team. training 그룹이 연결된 Team 만 쓴다(미연결이면 BE 가 422 를 반환한다). 같은
  // 그룹을 가리키는 Team 이 여럿이면 첫 Team 만 남긴다.
  const groupOptions = groupOptionsOf(groups);

  async function register(workspaceId: string): Promise<SharedWorkspaceWrite> {
    setBusy(true);
    try {
      const added = await addSharedWorkspace({ training_workspace_id: workspaceId });
      setShared((prev) => [...prev, added]);
      return { ok: true };
    } catch (error) {
      return { ok: false, message: resolveShareErrorMessage(error) };
    } finally {
      setBusy(false);
    }
  }

  async function remove(target: SharedWorkspace): Promise<SharedWorkspaceWrite> {
    setBusy(true);
    try {
      await removeSharedWorkspace(target.training_workspace_id);
      setShared((prev) =>
        prev.filter((row) => row.training_workspace_id !== target.training_workspace_id),
      );
      // 해제된 workspace 는 다시 미선점 후보다. 다시 조회하지 않고 목록에 되돌린다.
      setCandidates((prev) =>
        prev.some((candidate) => candidate.id === target.training_workspace_id)
          ? prev
          : [...prev, { id: target.training_workspace_id, name: target.workspace_name }],
      );
      return { ok: true };
    } catch (error) {
      return { ok: false, message: resolveLoadErrorMessage(error) };
    } finally {
      setBusy(false);
    }
  }

  async function createAndRegister(groupId: string, name: string): Promise<SharedWorkspaceCreate> {
    setBusy(true);
    let created: TrainingWorkspaceCandidate | null = null;
    try {
      created = await createTrainingWorkspace({ group_id: groupId, name });
      const added = await addSharedWorkspace({ training_workspace_id: created.id });
      setShared((prev) => [...prev, added]);
      return { ok: true };
    } catch (error) {
      const orphan = created;
      if (orphan !== null) {
        // 만든 workspace 를 후보에 추가한다. 그래서 호출부가 그것을 선택한 상태로 [등록]을 이어서 할 수 있다.
        setCandidates((prev) =>
          prev.some((candidate) => candidate.id === orphan.id) ? prev : [...prev, orphan],
        );
      }
      return { ok: false, message: resolveShareErrorMessage(error), orphan };
    } finally {
      setBusy(false);
    }
  }

  return {
    shared,
    candidateOptions,
    groupOptions,
    loading,
    loadError,
    candidateError,
    groupError,
    busy,
    // 새 Workspace 의 기본 Team.
    defaultGroupId: defaultCreateGroupId(shared, groups, groupOptions),
    teamNameOf: (trainingGroupId: string): string =>
      groups.find((group) => group.training_group_id === trainingGroupId)?.name ??
      trainingGroupId.slice(0, 8),
    register,
    remove,
    createAndRegister,
  };
}

function groupOptionsOf(groups: readonly TrainingGroup[]): GroupOption[] {
  const byGroupId = new Map<string, string>();
  for (const group of groups) {
    if (group.training_group_id !== null && !byGroupId.has(group.training_group_id)) {
      byGroupId.set(group.training_group_id, group.name);
    }
  }
  return [...byGroupId].map(([value, label]) => ({ value, label }));
}

// 새 Workspace 의 기본 Team. 이미 등록된 공용 workspace 의 그룹을 쓴다. 없으면 research Team,
// 그것도 없으면 미선택이다(사용자가 고른다).
function defaultCreateGroupId(
  shared: readonly SharedWorkspace[],
  groups: readonly TrainingGroup[],
  options: readonly GroupOption[],
): string | null {
  const optionIds = new Set(options.map((option) => option.value));
  const fromShared = shared.find((row) => optionIds.has(row.training_group_id));
  if (fromShared !== undefined) {
    return fromShared.training_group_id;
  }
  const research = groups.find(
    (group) =>
      group.training_group_id !== null &&
      optionIds.has(group.training_group_id) &&
      group.name.toLowerCase() === RESEARCH_TEAM_NAME,
  );
  return research?.training_group_id ?? null;
}

// 공용 등록 거절 사유. 코드마다 다음 행동이 다르다.
function resolveShareErrorMessage(error: unknown): string {
  if (isWorkspaceNotInScopeError(error)) {
    return "이 Workspace의 그룹이 Team에 연결되어 있지 않습니다. 관리자에게 Team 연결을 요청해 주세요.";
  }
  if (isWorkspaceAlreadyClaimedError(error)) {
    return "이미 멤버가 선점한 Workspace라 공용으로 쓸 수 없습니다. 다른 Workspace를 고르거나 새로 만들어 주세요.";
  }
  return resolveLoadErrorMessage(error);
}
